import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ClipboardList, ShieldCheck, ShieldAlert, ShieldX, Sparkles, Building2,
  HardHat, TrendingUp, FileText, Bell, ArrowRight, Star, CheckCircle2,
} from "lucide-react";

const STATUS_META = {
  current:       { tone: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", icon: ShieldCheck, label: "Current" },
  expiring_soon: { tone: "border-amber-500/40 text-amber-300 bg-amber-500/10",       icon: ShieldAlert, label: "Expiring soon" },
  critical:      { tone: "border-rose-500/40 text-rose-300 bg-rose-500/10",           icon: ShieldAlert, label: "Critical" },
  expired:       { tone: "border-rose-600/60 text-rose-300 bg-rose-600/15",           icon: ShieldX,     label: "Expired" },
  missing:       { tone: "border-rose-500/40 text-rose-300 bg-rose-500/10",           icon: ShieldX,     label: "Missing" },
  on_file:       { tone: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", icon: ShieldCheck, label: "On file" },
};

const PRIORITY_TONES = {
  urgent: "border-rose-500/40 text-rose-300",
  high:   "border-orange-500/40 text-orange-300",
  medium: "border-amber-500/40 text-amber-300",
  low:    "border-zinc-500/40 text-zinc-300",
};

export default function VendorHomePage() {
  const { user } = useAuth() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/vendor/home");
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadDoc = async (dtype) => {
    try {
      const res = await api.get(`/vendor/compliance/${dtype}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${dtype}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (loading && !data) {
    return <div className="text-sm text-zinc-500 p-6">Loading your dashboard…</div>;
  }
  if (!data) return null;

  const c = data.counts || {};
  const compl = data.compliance || {};
  const v = data.vendor || {};

  return (
    <div className="space-y-6" data-testid="vendor-home">
      <PageHeader
        eyebrow="Vendor portal"
        title={`Welcome back, ${v.name || user?.name || "there"}`}
        subtitle={v.onboarded_at
          ? `Onboarded ${(v.onboarded_at || "").slice(0, 10)} · ${v.category || "vendor"}${v.rating ? ` · ${v.rating}★` : ""}`
          : "Complete onboarding to unlock work orders."}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Open work orders" value={c.open_tickets ?? 0}
             sub={`${c.total_assigned ?? 0} total assigned`}
             tone={c.open_tickets > 0 ? "hot" : "default"} icon={ClipboardList} />
        <Kpi label="Open RFPs" value={c.open_rfps ?? 0}
             sub="tickets accepting bids" tone="default" icon={Sparkles} />
        <Kpi label="Properties served" value={c.properties_served ?? 0}
             sub={(data.properties || []).map((p) => p.name).slice(0, 2).join(" · ") || "—"}
             icon={Building2} />
        <Kpi label="COI status"
             value={STATUS_META[compl.coi?.status || "missing"]?.label}
             sub={compl.coi?.days_until_expiry != null
                   ? (compl.coi.days_until_expiry < 0
                       ? `Expired ${Math.abs(compl.coi.days_until_expiry)}d ago`
                       : `Expires in ${compl.coi.days_until_expiry}d`)
                   : "Upload required"}
             tone={compl.coi?.status === "current" ? "good"
                   : compl.coi?.status === "expiring_soon" ? "warn"
                   : "bad"} icon={ShieldCheck} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Compliance card */}
        <Card className="border-white/10 bg-card p-5 lg:col-span-1" data-testid="vendor-home-compliance">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Compliance</div>
            <Link to="/vendor/my-compliance" className="text-[10px] text-orange-300 hover:underline flex items-center gap-1">Manage <ArrowRight className="h-2.5 w-2.5" /></Link>
          </div>
          <div className="space-y-2.5">
            {["coi", "w9", "license"].map((k) => {
              const seg = compl[k] || {};
              const meta = STATUS_META[seg.status] || STATUS_META.missing;
              const Icon = meta.icon;
              const label = k === "w9" ? "W-9" : k === "coi" ? "COI" : "License";
              return (
                <div key={k} className={"rounded-md border p-3 " + meta.tone} data-testid={`vendor-home-compliance-${k}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5" />
                        <div className="font-medium text-sm">{label}</div>
                        <Badge variant="outline" className="text-[10px] border-current">{meta.label}</Badge>
                      </div>
                      <div className="text-[11px] opacity-80 mt-1">
                        {k === "coi" && seg.expires_at && `Expires ${(seg.expires_at || "").slice(0, 10)}`}
                        {k === "coi" && seg.carrier && ` · ${seg.carrier}`}
                        {k === "w9" && seg.signed_at && `Signed ${(seg.signed_at || "").slice(0, 10)}`}
                        {k === "license" && seg.number && `#${seg.number}`}
                        {k === "license" && seg.state && ` · ${seg.state}`}
                        {!seg.on_file && "Not uploaded"}
                      </div>
                    </div>
                    {seg.on_file && (
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(k)}
                              className="h-6 px-2 text-[10px]" data-testid={`vendor-home-dl-${k}`}>
                        <FileText className="h-3 w-3 mr-1" /> View
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {(compl.coi?.status === "expiring_soon" || compl.coi?.status === "critical" || compl.coi?.status === "expired") && (
            <div className="mt-3 text-[11px] text-amber-300 flex items-start gap-1.5">
              <Bell className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>Your PM will automatically resend an upload link at 60, 30, and 7 days before your COI expires. Watch your inbox for {v.email}.</span>
            </div>
          )}
        </Card>

        {/* Assigned tickets */}
        <Card className="border-white/10 bg-card p-5 lg:col-span-2" data-testid="vendor-home-tickets">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Recent work orders</div>
            <Link to="/vendor/tickets" className="text-[10px] text-orange-300 hover:underline flex items-center gap-1">All tickets <ArrowRight className="h-2.5 w-2.5" /></Link>
          </div>
          {(data.tickets || []).length === 0 && (
            <div className="text-center py-10 text-zinc-500 text-sm">
              <HardHat className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No assigned tickets yet.
              <div className="text-[11px] text-zinc-600 mt-1">Bid on open RFPs to get work.</div>
              <Button asChild size="sm" variant="outline" className="mt-3 border-white/10">
                <Link to="/vendor/rfp">Browse RFPs</Link>
              </Button>
            </div>
          )}
          <div className="divide-y divide-white/5">
            {(data.tickets || []).map((t) => (
              <div key={t.id} className="py-2.5 flex items-start justify-between gap-3" data-testid={`vendor-home-ticket-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm truncate max-w-[26rem]">{t.title}</div>
                    <Badge variant="outline" className={"text-[10px] " + (PRIORITY_TONES[t.priority] || PRIORITY_TONES.medium)}>{t.priority}</Badge>
                    <Badge variant="outline" className={"text-[10px] " +
                      (t.status === "resolved" ? "border-emerald-500/40 text-emerald-300"
                        : t.status === "in_progress" ? "border-indigo-500/40 text-indigo-300"
                        : "border-orange-500/40 text-orange-300")}>{t.status.replace("_", " ")}</Badge>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {t.category} · Unit {t.unit || "—"} · {(t.created_at || "").slice(0, 10)}
                  </div>
                </div>
                <Button asChild size="sm" variant="outline" className="border-white/10 flex-shrink-0 h-7 text-[11px]">
                  <Link to={`/vendor/tickets/${t.id}`}>Open <ArrowRight className="h-3 w-3 ml-1" /></Link>
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Property coverage strip */}
      {(data.properties || []).length > 0 && (
        <Card className="border-white/10 bg-card p-4" data-testid="vendor-home-properties">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Building2 className="h-3 w-3" /> Property coverage</div>
          <div className="flex flex-wrap gap-2">
            {data.properties.map((p) => (
              <Badge key={p.id} variant="outline" className="border-white/10 text-zinc-300">
                <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-400" /> {p.name}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Service categories */}
      {(v.service_categories || []).length > 0 && (
        <Card className="border-white/10 bg-card p-4" data-testid="vendor-home-services">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Star className="h-3 w-3 text-amber-400" /> You'll be suggested for</div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10">{v.category} (primary)</Badge>
            {v.service_categories.map((c) => (
              <Badge key={c} variant="outline" className="border-white/10 text-zinc-300">{c}</Badge>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "default", icon: Icon }) {
  const tones = {
    default: "border-white/10 bg-card",
    good:    "border-emerald-500/20 bg-emerald-500/[0.03]",
    warn:    "border-amber-500/20 bg-amber-500/[0.03]",
    bad:     "border-rose-500/20 bg-rose-500/[0.03]",
    hot:     "border-indigo-500/30 bg-indigo-500/[0.05]",
  };
  return (
    <Card className={"p-4 " + (tones[tone] || tones.default)}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-2xl font-semibold font-display mt-1">{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1 truncate">{sub}</div>}
    </Card>
  );
}
